import { array, boolean, object, string } from "yup";
import { dayjs } from "@thalamos/common";
import { defaultContextFields, generateDateBeforeNowValidationRule, nonEmptyRequiredString, nonEmptyString, } from "../shared.js";
export const outcomeFormCompletionStatusSchema = string()
    .oneOf(["not-started", "in-progress", "completed", "auto-closed"])
    .default("not-started");
export const monitoringFormPart1DataSchema = object({
    ...defaultContextFields({}),
    accommodationStatus: boolean().default(true),
    updateAccommodationStatus: array()
        .of(string().oneOf([
        "ownerOccupier",
        "tenant",
        "livingWithFamilyOrFriends",
        "universityOrCollege",
        "livingInCareHome",
        "homeless",
        "temporaryHousing",
        "admittedPatient",
        "criminalJusticeSettings",
        "other",
        "unknown",
    ]))
        .default([])
        .optional(),
    updateAccommodationStatusText: array().of(string()).optional(),
    descriptionOfAccommodationStatus: nonEmptyString.when("updateAccommodationStatus", {
        is: (val = []) => val.includes("other"),
        then: (schema) => schema.required("Please describe the accommodation status"),
        otherwise: (schema) => schema.notRequired(),
    }),
    personKnownMentalHealthServices: string()
        .oneOf(["true", "false", "unknown"], "Please select Yes, No, or Unknown")
        .required("Please confirm if person is known to the mental health service service")
        .default(null),
    nearestRelativeContactedValue: nonEmptyString
        .default("")
        .required("Please confirm if relative or friend has been informed "),
    nearestRelativeContactedText: nonEmptyRequiredString,
    dateTimeAmhpContacted: generateDateBeforeNowValidationRule("date-time", "Cannot enter a date in the future.").optional(),
    dateTimeAmhpArrived: generateDateBeforeNowValidationRule("date-time", "Cannot enter a date in the future.")
        .optional()
        .test({
        name: "amhpArrivedAfterFirstDoctorContacted",
        message: "AMHP cannot arrive before being contacted",
        test: function (value, { parent }) {
            const amhpContacted = parent.dateTimeAmhpContacted;
            const amhpArrived = value;
            if (!amhpContacted || !amhpArrived) {
                return true;
            }
            const amhpContactedDate = dayjs(amhpContacted);
            const amhpArrivedDate = dayjs(amhpArrived);
            return amhpArrivedDate.isSameOrAfter(amhpContactedDate);
        },
    }),
    dateTimeFirstDoctorContacted: generateDateBeforeNowValidationRule("date-time", "Cannot enter a date in the future.").optional(),
    dateTimeFirstDoctorArrived: generateDateBeforeNowValidationRule("date-time", "Cannot enter a date in the future.")
        .optional()
        .test({
        name: "firstDoctorArrivedAfterFirstDoctorContacted",
        message: "First doctor cannot arrive before being contacted",
        test: function (value, { parent }) {
            const firstDoctorContacted = parent.dateTimeFirstDoctorContacted;
            const firstDoctorArrived = value;
            if (!firstDoctorContacted || !firstDoctorArrived) {
                return true;
            }
            const firstDoctorContactedDate = dayjs(firstDoctorContacted);
            const firstDoctorArriveddDate = dayjs(firstDoctorArrived);
            return firstDoctorArriveddDate.isSameOrAfter(firstDoctorContactedDate);
        },
    }),
    dateTimeAssessmentCompleted: generateDateBeforeNowValidationRule("date-time", "Cannot enter a date in the future.").optional(),
    assessmentExtended: boolean()
        .default(null)
        .required("Please select whether the assessment was extended"),
    whyWasAssessmentExtendedText: nonEmptyString.when("assessmentExtended", {
        is: (value) => value,
        then: () => nonEmptyString.required("Reason for extension must be provided if assessment was extended"),
        otherwise: () => string(),
    }),
    whyWasAssessmentExtendedValue: nonEmptyString.when("assessmentExtended", {
        is: (value) => value,
        then: () => nonEmptyString.required("Reason for extension must be provided if assessment was extended"),
        otherwise: () => string(),
    }),
    whyWasAssessmentExtendedOtherDescription: nonEmptyString.when("whyWasAssessmentExtendedValue", {
        is: (value) => value === "other",
        then: () => nonEmptyString.required("Please provide a description if 'Other' was selected"),
        otherwise: () => string(),
    }),
    dateTimeDetentionEnded: generateDateBeforeNowValidationRule("date-time", "Cannot enter a date in the future.").required("Please confirm date & time of 136 detention end"),
    outcomeOfIncident: boolean().default(true),
    updateOutcomeOfIncident: string()
        .default(null)
        .oneOf([
        "136Expired",
        "mhaAdmission",
        "admissionInformally",
        "mhFollowUp",
        "primaryCareFollowUp",
        "noFollowUp",
        "custodyTransfer",
        "physicalHealthcareTransfer",
        "other",
    ])
        .required("Please confirm outcome of 136 incident"),
    updateOutcomeOfIncidentText: string(),
    dateTimePersonAdmitted: generateDateBeforeNowValidationRule("date-time", "Cannot enter a date in the future.").when("updateOutcomeOfIncident", {
        is: (val) => val === "136Expired" ||
            val === "mhaAdmission" ||
            val === "admissionInformally",
        then: (schema) => schema.required("Please confirm date of transfer or admission"),
        otherwise: (schema) => schema.notRequired(),
    }),
    descriptionOfOutcome: nonEmptyString.when("updateOutcomeOfIncident", {
        is: (val) => val === "other",
        then: (schema) => schema.required("Please describe outcome of 136 incident"),
        otherwise: (schema) => schema.notRequired(),
    }),
    // --- Hidden fields (not rendered in form builder, set programmatically) ---
    /** Tracks the lifecycle of the outcome form. Set by server-side logic. */
    outcomeCompletionStatus: outcomeFormCompletionStatusSchema,
    /** ISO date string when the form was auto-closed by the background job. */
    autoClosedAt: string().nullable().default(null),
    /** User ID of the last user who owned the draft (for auto-close attribution). */
    lastOwnerUserId: string().nullable().default(null),
    /** Display name of the last user who owned the draft. */
    lastOwnerUserName: string().nullable().default(null),
    // --- Place of safety fields (populated server-side when draft is created) ---
    /** Name of the place of safety linked to this outcome form. */
    placeOfSafetyName: string().nullable().default(null),
    /** Address line of the place of safety. */
    placeOfSafetyAddress: string().nullable().default(null),
    /** Postal code of the place of safety. */
    placeOfSafetyPostalCode: string().nullable().default(null),
    /**
     * The curated identity of this place of safety: the curated Place of Safety
     * id when it is a curated POS, or null when it was entered manually. Retaining
     * it means the place of safety keeps its curated identity (and the Trust that
     * comes with it) wherever it is used, rather than being treated as ad-hoc.
     */
    placeOfSafetyCuratedId: string().nullable().default(null),
    /**
     * The Trust responsible for this place of safety, or null when there is none
     * (a manually-entered POS has no associated Trust). This is the Trust that
     * follows the place of safety when it is the one in effect.
     */
    placeOfSafetyTrustId: string().nullable().default(null),
});
//# sourceMappingURL=monitoringForm.js.map